/**
 * Minimal RFC 4180 CSV reader/writer. Hand-rolled rather than pulling in a dependency: the
 * grammar is small (quoted fields, doubled quotes, embedded delimiters/newlines) and every
 * branch is covered by csv.test.ts. The reader is deliberately lenient about what real-world
 * spreadsheet exports produce (UTF-8 BOM, CRLF/LF/CR line endings, `;` or tab delimiters).
 */

export type CsvDelimiter = "," | ";" | "\t";

const CANDIDATE_DELIMITERS: CsvDelimiter[] = [",", ";", "\t"];

export const UTF8_BOM = "﻿";

export interface ParsedCsv {
  rows: string[][];
  delimiter: CsvDelimiter;
  /** True when the input ended inside a quoted field (the file is likely truncated or malformed). */
  unterminatedQuote: boolean;
}

/**
 * Guess the delimiter from the first record, counting only characters outside quotes.
 * Comma wins ties so ordinary CSVs are never misread.
 */
export const detectCsvDelimiter = (text: string): CsvDelimiter => {
  const counts: Record<CsvDelimiter, number> = { ",": 0, ";": 0, "\t": 0 };
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === "\n" || ch === "\r") break;
    if (ch === "," || ch === ";" || ch === "\t") counts[ch]++;
  }
  let best: CsvDelimiter = ",";
  for (const candidate of CANDIDATE_DELIMITERS) {
    if (counts[candidate] > counts[best]) best = candidate;
  }
  return best;
};

/** Parse CSV text into rows of raw string cells. Blank lines are dropped. */
export const parseCsv = (input: string, options: { delimiter?: CsvDelimiter } = {}): ParsedCsv => {
  const text = input.startsWith(UTF8_BOM) ? input.slice(1) : input;
  const delimiter = options.delimiter ?? detectCsvDelimiter(text);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;

  const endRow = () => {
    row.push(field);
    field = "";
    if (row.some((cell) => cell.trim() !== "")) rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }

    if (ch === '"' && field === "") {
      inQuotes = true;
      i++;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
      i++;
    } else if (ch === "\r" || ch === "\n") {
      endRow();
      i += ch === "\r" && text[i + 1] === "\n" ? 2 : 1;
    } else {
      field += ch;
      i++;
    }
  }

  const unterminatedQuote = inQuotes;
  if (field !== "" || row.length > 0) endRow();

  return { rows, delimiter, unterminatedQuote };
};

const needsQuoting = (value: string, delimiter: string) =>
  value.includes(delimiter) ||
  value.includes('"') ||
  value.includes("\n") ||
  value.includes("\r") ||
  value !== value.trim();

/** Quote a single cell per RFC 4180 when it contains the delimiter, a quote, a newline or edge whitespace. */
export const quoteCsvField = (value: string, delimiter: string = ","): string =>
  needsQuoting(value, delimiter) ? `"${value.replace(/"/g, '""')}"` : value;

/** Serialize rows to CSV with CRLF record separators (RFC 4180). */
export const serializeCsv = (
  rows: readonly (readonly string[])[],
  options: { delimiter?: CsvDelimiter } = {}
): string => {
  const delimiter = options.delimiter ?? ",";
  return rows.map((row) => row.map((cell) => quoteCsvField(cell, delimiter)).join(delimiter)).join("\r\n");
};

// Spreadsheet apps evaluate cells starting with these as formulas (CSV/formula injection).
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

/**
 * Neutralize free-text cells that a spreadsheet would treat as a formula by prefixing an
 * apostrophe (hidden by Excel/Sheets). Values that already start with an apostrophe get one
 * more too, so `unescapeSpreadsheetText` is an exact inverse.
 */
export const escapeSpreadsheetText = (value: string): string =>
  FORMULA_TRIGGER.test(value) || value.startsWith("'") ? `'${value}` : value;

export const unescapeSpreadsheetText = (value: string): string =>
  value.startsWith("'") && (FORMULA_TRIGGER.test(value.slice(1)) || value[1] === "'")
    ? value.slice(1)
    : value;
